import track from 'react-tracking';
import { Row, Column } from '@simplywallst/ui-core';
import {
  ControlsContainer,
  FilterColumn,
  FilterColumnRule,
  FilterLabel,
  FiltersContainer,
  SnowflakeColumn,
} from './styled';
import { Button } from '@simplywallst/ui-core';
import SnowflakeConfig from '../SnowflakeConfig';
import If from '@/components/If';
import Tags from '../Tags';

interface Props {
  canSaveScreener: boolean;
  isEditingUserSavedScreener: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onDelete?: () => void;
  onSave?: () => void;
  onReset?: () => void;
  onEditAddClick?: () => void;
  onFiltersBadgesClick?: () => void;
  onIndustryBadgesClick?: () => void;
  onMarketBadgesClick?: () => void;
  onShowFiltersModal?: () => void;
  onShowIndustryModal?: () => void;
  onShowMarketModal?: () => void;
  onSnowflakeChange?: (points: number[]) => void;
  selectedFilters: string[];
  selectedIndustries: string[];
  selectedMarkets: string[];
  snowflakeScore: number[];
}

const ControlGroup = ({
  canSaveScreener,
  isEditingUserSavedScreener,
  isSaving,
  isDeleting,
  onDelete,
  onFiltersBadgesClick,
  onIndustryBadgesClick,
  onMarketBadgesClick,
  onReset,
  onSave,
  onShowFiltersModal,
  onShowIndustryModal,
  onShowMarketModal,
  onSnowflakeChange,
  selectedFilters,
  selectedIndustries,
  selectedMarkets,
  snowflakeScore,
  tracking: { trackEvent },
}: Props & Tracking) => {
  const concatMarketList = selectedMarkets.length > 16;
  const concatIndustryList = selectedIndustries.length > 16;
  const concatFilters = selectedFilters.length > 16;
  return (
    <FiltersContainer>
      <Row>
        <FilterColumn colMd={3} colSm={12}>
          <Row>
            <Column displayFlex col={6} colMd={12} alignItem="center">
              <FilterLabel>Filter by Market</FilterLabel>
            </Column>
            <Column
              displayFlex
              col={6}
              colMd={12}
              alignContent="center"
              justifyContentMd="flex-start"
              justifyContent="flex-end"
            >
              <Button
                styleType="louder"
                onClick={() => {
                  onShowMarketModal && onShowMarketModal();
                  trackEvent({
                    action: 'click',
                    subject: 'MarketFilter',
                  });
                }}
                data-cy-id="add-market"
              >
                {selectedMarkets.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </Column>
          </Row>
          <If match={selectedMarkets.length > 0}>
            <Row>
              <Tags
                list={selectedMarkets}
                concat={concatMarketList}
                onClick={onMarketBadgesClick}
              />
            </Row>
          </If>
          <FilterColumnRule />
        </FilterColumn>
        <FilterColumn colMd={3} colSm={12}>
          <Row>
            <Column displayFlex col={6} colMd={12} alignItem="center">
              <FilterLabel>Filter by Industry</FilterLabel>
            </Column>
            <Column
              displayFlex
              col={6}
              colMd={12}
              alignContent="center"
              justifyContentMd="flex-start"
              justifyContent="flex-end"
            >
              <Button
                styleType="louder"
                onClick={() => {
                  onShowIndustryModal && onShowIndustryModal();
                  trackEvent({
                    action: 'click',
                    subject: 'IndustryFilter',
                  });
                }}
                data-cy-id="add-industry"
              >
                {selectedIndustries.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </Column>
          </Row>
          <If match={selectedIndustries.length !== 0}>
            <Row>
              <Tags
                list={selectedIndustries}
                concat={concatIndustryList}
                onClick={onIndustryBadgesClick}
              />
            </Row>
          </If>
          <FilterColumnRule />
        </FilterColumn>
        <FilterColumn colMd={3} colSm={12}>
          <Row>
            <Column displayFlex col={6} colMd={12} alignItem="center">
              <FilterLabel>Advanced Filters</FilterLabel>
            </Column>
            <Column
              displayFlex
              col={6}
              colMd={12}
              alignContent="center"
              justifyContentMd="flex-start"
              justifyContent="flex-end"
            >
              <Button
                styleType="louder"
                onClick={() => {
                  onShowFiltersModal && onShowFiltersModal();
                  trackEvent({
                    action: 'click',
                    subject: 'AdvancedFilter',
                  });
                }}
                data-cy-id="add-filters"
              >
                {selectedFilters.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </Column>
          </Row>
          <If match={selectedFilters.length !== 0}>
            <Row>
              <Tags
                list={selectedFilters}
                concat={concatFilters}
                onClick={onFiltersBadgesClick}
              />
            </Row>
          </If>
          <FilterColumnRule />
        </FilterColumn>
        <FilterColumn colMd={3} colSm={12}>
          <Row>
            <Column>
              <FilterLabel>Filter by Snowflake</FilterLabel>
            </Column>
            <SnowflakeColumn>
              <SnowflakeConfig
                score={snowflakeScore}
                onChange={onSnowflakeChange}
              />
            </SnowflakeColumn>
          </Row>
        </FilterColumn>
      </Row>
      <Row>
        <ControlsContainer
          displayFlex
          alignContent="center"
          alignItem="center"
          justifyContentSm="flex-end"
        >
          <If match={canSaveScreener && isEditingUserSavedScreener}>
            <Button
              styleType="gentle"
              onClick={() => {
                onDelete && onDelete();
                trackEvent({
                  action: 'click',
                  subject: 'delete',
                });
              }}
              disabled={isSaving || isDeleting}
              isLoading={isDeleting}
              data-cy-id="delete"
            >
              Delete
            </Button>
          </If>
          <Button
            styleType="gentle"
            onClick={() => {
              onReset && onReset();
              trackEvent({
                action: 'click',
                subject: 'reset',
              });
            }}
            disabled={isSaving || isDeleting}
            data-cy-id="reset-filters"
          >
            Reset
          </Button>
          <Button
            styleType="louder"
            onClick={() => {
              onSave && onSave();
              trackEvent({
                action: 'click',
                subject: 'save',
              });
            }}
            disabled={isSaving || isDeleting}
            isLoading={isSaving}
            data-cy-id="save"
          >
            {isEditingUserSavedScreener ? 'Save Changes' : 'Save Screener'}
          </Button>
        </ControlsContainer>
      </Row>
    </FiltersContainer>
  );
};

export default track({
  type: 'track',
})(ControlGroup);
